import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { Injectable, Optional } from '@angular/core';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | false | null>(null);

  private excludedUrls = [
    'https://login.cyncly.com',
    'https://test-login.cyncly.com',
    'https://dev-login.cyncly.com',
    '/.well-known/openid-configuration',
    '/connect/token',
    '/connect/authorize',
    '/connect/userinfo',
    '/connect/endsession'
  ];

  public constructor(@Optional() private authService: AuthService) {  }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldExcludeUrl(req.url)) {
      return next.handle(req);
    }

    const shouldAuthorize = this.shouldAuthorizeUrl(req.url);
    let request = req;

    if (shouldAuthorize && this.authService?.isAuthenticated()) {
      const token = this.authService.getAccessToken();
      if (token) {
        request = this.addTokenToRequest(req, token);
      }
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && shouldAuthorize && this.authService) {
          if (this.authService.isAuthenticated() && this.authService.canRefreshToken()) {
            return this.handle401Error(request, next);
          }

          this.authService.expireSession();
          return throwError(() => error);
        }

        return throwError(() => error);
      })
    );
  }

  private shouldAuthorizeUrl(url: string): boolean {
    return Object.values(environment.apiUris).filter(Boolean).some(apiUrl => url.startsWith(apiUrl));
  }

  private shouldExcludeUrl(url: string): boolean {
    return this.excludedUrls.some(excludedUrl => url.includes(excludedUrl));
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return new Observable(observer => {
        this.authService.refreshToken().then(success => {
          this.isRefreshing = false;

          if (!success) {
            this.refreshTokenSubject.next(false);
            this.authService.expireSession();
            observer.error(new Error('Sessão expirada. Faça login novamente.'));
            return;
          }

          const newToken = this.authService.getAccessToken();
          if (!newToken) {
            this.refreshTokenSubject.next(false);
            this.authService.expireSession();
            observer.error(new Error('Sessão expirada. Faça login novamente.'));
            return;
          }

          this.refreshTokenSubject.next(newToken);
          next.handle(this.addTokenToRequest(request, newToken)).subscribe({
            next: event => observer.next(event),
            error: refreshError => observer.error(refreshError),
            complete: () => observer.complete()
          });
        }).catch(error => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);
          this.authService.expireSession();
          observer.error(error);
        });
      });
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        if (token === false) {
          return throwError(() => new Error('Sessão expirada. Faça login novamente.'));
        }
        return next.handle(this.addTokenToRequest(request, token));
      })
    );
  }
}
